/* eslint-disable react-hooks/exhaustive-deps */
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Badge,
  Box,
  Button,
  colors,
  Grid,
  Typography,
} from "@material-ui/core";
import { StyledForm } from "components/List/StyledComponents";
import { OptionTitle, StyledPaper } from "components/Styled";
import apiRequest, { fetcher } from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import KeyValueList from "./KeyValueList";
import { StyledTextField } from "../Styled";
import {
  Dataset,
  DatasetSyncConfig,
  DatasetSyncRunSummary,
} from "@dashboard-v3/api";
import { DatasetReqBody } from "../types";
import useSWR from "swr";
import { PropsWithChildren, Suspense, useState } from "react";
import * as Sentry from "@sentry/react";
import { getUser } from "utils/authentication";
import ButtonWithLoading from "components/ButtonWithLoading";
import produce from "immer";
import LinearLoader from "components/LinearLoader";
import SectionTabs from "components/SectionTabs";
import SyncRuns from "../Sync/SyncRuns";
import SyncConfigForm from "../Sync/SyncConfigForm";
import buildSyncForm from "./buildSyncForm";

export default function Loader() {
  return (
    <Suspense fallback={<LinearLoader />}>
      <DataSetsForm />
    </Suspense>
  );
}

const isInvalidName = isBlank;

function DataSetsForm() {
  const { t } = useTranslation("datasets");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { id: paramId } = useParams();
  const isEditing = Boolean(paramId);
  const [showTab, setShowTab] = useState("items");
  const [isSaving, setIsSaving] = useState(false);
  const [errorInRows, setErrorInRows] = useState(isEditing ? false : true);
  const [nameTouched, setNameTouched] = useState(false);
  const { data: dataset } = useSWR<Dataset>(
    paramId ? `/datasets/${paramId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      suspense: true,
      onError() {
        enqueueSnackbar(t("form.errors.fetchByIdError"), {
          variant: "error",
        });
        history.push("/datasets");
      },
    }
  );

  const [form, setForm] = useState<DatasetReqBody>(
    toForm(dataset) || { name: "", rows: {}, id: null }
  );
  const canSave = !errorInRows && !isInvalidName(form.name);

  const saveDataset = async event => {
    event.preventDefault();

    if (!canSave) return false;

    try {
      setIsSaving(true);

      if (isEditing) {
        await apiRequest<Dataset>("PUT", `/datasets/${form.id}`, form);
        enqueueSnackbar(t("updateSuccess", { name: form.name }), {
          variant: "success",
        });
      } else {
        await apiRequest<Dataset>("POST", "/datasets", form);
        enqueueSnackbar(t("createSuccess", { name: form.name }), {
          variant: "success",
        });
      }

      return history.push("/datasets");
    } catch (e) {
      const errorMsg = form.id ? "updateError" : "createError";
      enqueueSnackbar(t(`errors.${errorMsg}`), {
        variant: "error",
      });

      Sentry.captureException(e, { user: getUser(), extra: { changes: form } });
    } finally {
      setIsSaving(false);
    }
  };

  function updateName(newName: string) {
    setForm(
      produce(draft => {
        draft.name = newName;
      })
    );
  }

  function updateRows(rows: Record<string, string[]>, areRowValids: boolean) {
    setForm(
      produce(draft => {
        draft.rows = rows;
      })
    );
    setErrorInRows(!areRowValids);
  }

  /**
   * Misma key que usa `SyncRuns`, asi que esto no agrega un request: SWR
   * comparte la cache, y el badge se apaga solo cuando el polling de esa lista
   * trae un run que termino bien.
   */
  const { data: syncRuns } = useSWR<DatasetSyncRunSummary[]>(
    isEditing ? `/datasets/${paramId}/dataset-sync-runs` : null,
    fetcher
  );

  // Los dos estados terminales en los que el sync quedo parado, iguales a los
  // que pintan el badge rojo en la lista.
  const lastRunFailed =
    syncRuns?.[0]?.status === "ERROR" ||
    syncRuns?.[0]?.status === "WAITING_RETRY_CANCELED";

  const tabs = isEditing
    ? [
        { id: "items", label: t("form.tab_items") },
        { id: "syncConfig", label: t("form.tab_syncConfig") },
        {
          id: "syncTasks",
          label: lastRunFailed ? (
            <FailedSyncLabel>{t("form.tab_syncs")}</FailedSyncLabel>
          ) : (
            t("form.tab_syncs")
          ),
        },
      ]
    : [
        { id: "items", label: t("form.tab_items") },
        { id: "syncConfig", label: t("form.tab_fillWith") },
      ];

  return (
    <Grid container>
      <Grid item xs={8}>
        <StyledPaper>
          <StyledForm onSubmit={saveDataset} data-testid="dataset-form">
            <Typography variant="h6">
              {isEditing ? t("form.editTitle") : t("form.title")}
            </Typography>
            <NameInput
              name={form.name}
              touched={nameTouched}
              onChange={updateName}
              onBlur={() => setNameTouched(true)}
            />
            <SectionTabs
              tabs={tabs}
              onClick={setShowTab}
              showTab={showTab}
              data-testid="dataset-form-tab"
            />
            <Box role="tabpanel" hidden={showTab !== "items"}>
              <KeyValueList rows={form.rows} onChange={updateRows} />
              <Box display="flex" alignItems="center" gridGap="1.5rem" mt={4}>
                <ActionBtn
                  disabled={!canSave}
                  loading={isSaving}
                  data-testid="dataset-form-save-btn"
                >
                  {t("saveDataset")}
                </ActionBtn>
                {!isEditing && (
                  <>
                    <Box fontWeight="500"> {t("syncForm.or")} </Box>
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      data-testid="dataset-form-fill-with-btn"
                      onClick={event => {
                        event.preventDefault();
                        setShowTab("syncConfig");
                      }}
                    >
                      {t("form.fillWith")}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            <Box role="tabpanel" hidden={showTab !== "syncConfig"}>
              <SyncConfig
                datasetName={form.name}
                datasetId={form.id}
                rows={form.rows}
              />
            </Box>
            {isEditing && (
              <Box role="syncTasks" hidden={showTab !== "syncTasks"}>
                <SyncRuns datasetId={form.id} />
              </Box>
            )}
          </StyledForm>
        </StyledPaper>
      </Grid>
    </Grid>
  );
}

function SyncConfig({
  datasetName,
  datasetId,
  rows,
}: {
  datasetName: string;
  datasetId?: string;
  rows?: Record<string, string[]>;
}) {
  const { t } = useTranslation("datasets");

  const { data: sync, isLoading } = useSWR<DatasetSyncConfig>(
    datasetId ? `/datasets/${datasetId}/dataset-syncs` : null,
    fetcher
  );

  if (isLoading) return <>Loading...</>;

  const form = buildSyncForm({ datasetName, datasetId, rows, sync });

  return (
    <>
      <Box mb={4}>
        <OptionTitle>{t("syncForm.title")}</OptionTitle>

        <Box mt={1} color={colors.grey[600]}>
          {t("syncForm.subtitle")}
        </Box>
      </Box>

      <SyncConfigForm data={form} />
    </>
  );
}

function ActionBtn({
  loading = false,
  disabled,
  children,
  ...rest
}: PropsWithChildren<{
  loading: boolean;
  disabled: boolean;
  "data-testid"?: string;
}>) {
  return (
    <ButtonWithLoading
      type="submit"
      color="primary"
      variant="contained"
      disableElevation
      disabled={disabled}
      loading={loading}
      {...rest}
    >
      {children}
    </ButtonWithLoading>
  );
}

function toForm(dataset?: Dataset): DatasetReqBody {
  if (!dataset) return null;

  return {
    id: dataset.id,
    name: dataset.name,
    rows: dataset.rows || {},
  };
}

function NameInput({
  name,
  touched,
  onChange,
  onBlur,
}: {
  name: string;
  touched: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const { t } = useTranslation("datasets");
  const invalid = isInvalidName(name);
  const showError = touched && invalid;
  const nameErrorHint = showError ? t("form.fields.name.errors.empty") : "";

  return (
    <StyledTextField
      inputProps={{ "data-testid": "dataset-form-name-input" }}
      name="name"
      variant="outlined"
      fullWidth
      label={t("form.fields.name.label")}
      value={name}
      error={showError}
      helperText={nameErrorHint}
      onChange={event => {
        const newName = event.target.value;
        onChange(newName);
      }}
      onBlur={onBlur}
    />
  );
}

/**
 * El punto rojo que dice que el ultimo sync fallo (MXH-5792).
 *
 * Va en el tab y no adentro porque el tab es lo unico que se ve con el panel
 * cerrado: el usuario entra al dataset por otra cosa -corregir la carpeta,
 * mirar los items- y hasta ahora no habia nada que lo mandara a mirar los runs.
 *
 * `WAITING_RETRY` no lo enciende: ahi el sync se esta reintentando solo y
 * todavia puede terminar bien, igual que en la lista.
 */
function FailedSyncLabel({ children }: { children: React.ReactNode }) {
  return (
    <Badge
      color="secondary"
      variant="dot"
      style={{ paddingRight: "8px" }}
      data-testid="dataset-form-sync-failed-badge"
    >
      {children}
    </Badge>
  );
}
